/**
 * Typed client for the AGENT CHANNELS read surface (`channels.*` gateway
 * methods) — the data layer behind the Agent-page Telegram/WhatsApp tabs.
 *
 * Contract source: the C1 runtime handback
 * (`ai-docs/handoffs/agent-channels-c1-lexruntime-handback-2026-07-21.md`,
 * commits `60ddd52e` + `5fde09e7`) and the impl plan
 * (`ai-docs/lexide-agent-channel-tabs-impl-plan.md` §3.1). FROZEN + read-only.
 *
 * ## Read-only BY CHOICE (it used to be by construction)
 * ⚠ UPDATED 2026-07-23 — the runtime lane shipped `channels.reply`, an
 * ATTRIBUTED lawyer write (`{channel, peer, text}` → `{ts, authoredBy:
 * "lawyer"}`, `whatsapp`/`telegram` only, requires an inbound row on that
 * channel). So the old claim here — "no write verb exists" — is no longer true,
 * and this client deliberately does not bind it: the reply composer is tabs-plan
 * P3, which needs its own approval before it ships.
 *
 * The invariant that actually holds is the restated one: there is no
 * *unattributed, unaudited, agent-reachable* write verb. Outbound rows may now
 * carry `authoredBy: "lawyer"`; its ABSENCE means the agent wrote it (every
 * pre-T3 row), so read attribution off the positive value only.
 *
 * ## No local fixture — deliberately
 * The sibling tasklist client had a build-phase fixture backend that
 * re-implemented server behaviour; every place it drifted became a defect the
 * tests could not see (it masked a non-canonical sort order AND a filter-shape
 * violation). So there is NO stand-in here: a runtime that does not serve
 * `channels.*` is surfaced honestly by `useChannelsInventory`'s gate (the pane
 * keeps its placeholder), never by simulating data.
 */
import { useGatewayStore } from "@/stores/useGatewayStore";

/** A channel slot as the tab needs it (`channels.list` row). */
export interface ChannelSummary {
  id: string;
  /**
   * `connected | pending | error | disconnected` from the live resolver.
   * THE presence signal — the row is ALWAYS present (total inventory,
   * `5fde09e7`), so filter on this, never on row absence. Means CONFIGURED,
   * not healthy: a bare creds file resolves to `connected`. Never render it as
   * a health light. (`email` is hard-coded `disconnected` — its real presence
   * signal is mailbox credentials, not this field.)
   */
  status: string;
  /** True when this channel has at least one recorded (stamped) message. */
  hasActivity: boolean;
}

/** One peer's conversation summary (`channels.conversations` row). */
export interface ChannelConversation {
  /** `null` groups rows whose peer stash was missed — render, never drop. */
  peer: string | null;
  peerName?: string;
  lastTs: number;
  /** Server-bounded to 280 chars, ellipsised. */
  lastText: string;
  messageCount: number;
  /** Raw LX session keys — one peer can span several case scopes. */
  scopes: string[];
}

/**
 * The `ConversationMessage` subset the thread renders (`channels.messages`
 * row). Every row this surface sees carries a `channel` by construction
 * (the store filters on it), so the "absent channel ⇒ web turn" discriminator
 * never occurs inside these tabs.
 */
export interface ChannelMessage {
  /** `user` = inbound peer message; `assistant` = the agent's reply. */
  role: "user" | "assistant";
  text: string;
  ts: number;
  runId: string;
  /** Raw LX session key (drives media fetch + the case-scope chip). */
  scope: string;
  channel?: string;
  peer?: string;
  peerName?: string;
  /** Set on a failed assistant turn (text may be empty). */
  error?: string;
  attachments?: {
    kind: "file";
    path: string;
    name: string;
    mime?: string;
    sizeBytes?: number;
  }[];
}

export interface ChannelMessagesParams {
  channel: string;
  peer?: string;
  /** Backwards ts cursor: return rows strictly older than this. */
  before?: number;
  /** Page size; server default 100, clamped at 500. */
  limit?: number;
}

export interface ChannelMessagesResult {
  channel: string;
  peer?: string;
  /** Render order (OLDEST first). */
  messages: ChannelMessage[];
  /**
   * ⚠ Matching rows at/below the CURRENT cursor — equals the full thread size
   * only on the head fetch (no `before`) and SHRINKS as you page backwards.
   * Drive "load older" from `nextBefore` presence, never arithmetic on `total`.
   */
  total: number;
  /** Cursor for the next older page; ABSENT ⇒ the head is reached. */
  nextBefore?: number;
}

function rpc() {
  const client = useGatewayStore.getState().rpc;
  if (!client) throw new Error("gateway not connected");
  return client;
}

/** Cast typed params to the rpc.call bag shape (mirrors lx-tasklist's `asParams`). */
const asParams = (o: unknown): Record<string, unknown> => o as Record<string, unknown>;

/** `channels.list` — the total inventory (one row per canonical id + any the
 *  runtime reports), with live status + activity. */
export function listChannels(): Promise<{ channels: ChannelSummary[] }> {
  return rpc().call<{ channels: ChannelSummary[] }>("channels.list", {});
}

/** `channels.conversations` — the peers this channel has talked to, newest
 *  first, `peer:null` bucket included. */
export function listConversations(
  channel: string,
): Promise<{ channel: string; conversations: ChannelConversation[] }> {
  return rpc().call<{ channel: string; conversations: ChannelConversation[] }>(
    "channels.conversations",
    asParams({ channel }),
  );
}

/** `channels.messages` — one thread (optionally one peer), oldest-first,
 *  paged backwards on `before`/`nextBefore`. */
export function listMessages(
  params: ChannelMessagesParams,
): Promise<ChannelMessagesResult> {
  return rpc().call<ChannelMessagesResult>("channels.messages", asParams(params));
}
